import {
  DashboardAttentionEngagementClient,
  DashboardAttentionEngagementStatus,
} from "../api/contracts";
import { PillPaletteToken } from "./palette";

export interface EngagementStatusPresentation {
  label: string;
  definition: string;
}

export const engagementStatusPresentation: Record<
  DashboardAttentionEngagementStatus,
  EngagementStatusPresentation
> = {
  "on-track": {
    label: "On Track",
    definition:
      "Clients counted here completed at least 60% of all homework assigned.",
  },
  "needs-follow-up": {
    label: "Needs Follow-Up",
    definition: "Clients counted here completed less than 60% of all homework.",
  },
  "no-recent-assistant-use": {
    label: "Needs Outreach",
    definition:
      "Clients counted here have not typed or spoken a response to the OCD Homework Assistant in the last 7 days.",
  },
  "new-client": {
    label: "New Client",
    definition:
      "Clients counted here have never been assigned homework in the OCD Homework Assistant.",
  },
};

export const engagementStatusOrder = [
  "on-track",
  "needs-follow-up",
  "no-recent-assistant-use",
  "new-client",
] as const satisfies readonly DashboardAttentionEngagementStatus[];

export const engagementStatusPillToken: Record<
  DashboardAttentionEngagementStatus,
  PillPaletteToken
> = {
  "on-track": "greenLight1",
  "needs-follow-up": "yellowLight1",
  "no-recent-assistant-use": "orangeLight1",
  "new-client": "blueLight1",
};

export function formatLastAssistantUse(
  client: DashboardAttentionEngagementClient,
): string {
  if (client.lastAssistantUseAt === null) return "none recorded";
  if (client.daysSinceAssistantUse === 0) return "today";
  if (client.daysSinceAssistantUse === 1) return "1 day ago";
  return `${client.daysSinceAssistantUse} days ago`;
}

export function formatHomeworkCompletion(
  client: DashboardAttentionEngagementClient,
): string {
  if (client.homeworkAssignedCount === 0) return "No homework assigned";
  const rate = formatCompletionRate(client);
  return `${rate}% (${client.homeworkCompletedCount} of ${client.homeworkAssignedCount})`;
}

function formatCompletionRate(
  client: DashboardAttentionEngagementClient,
): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  }).format(client.completionRate ?? 0);
}

export function engagementClientReason(
  client: DashboardAttentionEngagementClient,
): string {
  return `Last assistant use: ${formatLastAssistantUse(client)} · Homework: ${formatHomeworkCompletion(client)}`;
}

export function engagementSignalReason(
  client: DashboardAttentionEngagementClient,
): string {
  if (client.engagementStatus === "new-client") {
    return "No homework assigned yet";
  }

  if (client.engagementStatus === "no-recent-assistant-use") {
    return "Client has not typed or spoken a response to the OCD Homework Assistant in the last 7 days.";
  }

  const completion = `Completed ${formatCompletionRate(client)}% of assigned homework (${client.homeworkCompletedCount} of ${client.homeworkAssignedCount})`;
  return client.engagementStatus === "on-track"
    ? `${completion}, meeting the 60% target.`
    : `${completion}, below the 60% target.`;
}
